import * as React from "react";
import { SECTION_LABEL } from "./SectionLabel";

/** The browser's resolved IANA timezone, or "UTC" if unavailable. */
export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

/** The full IANA timezone list when the runtime supports it, else a small fallback. */
function timezoneOptions(): string[] {
  try {
    const intl = Intl as unknown as { supportedValuesOf?: (k: string) => string[] };
    const all = intl.supportedValuesOf?.("timeZone");
    if (all && all.length) return all;
  } catch {
    // fall through
  }
  const here = browserTimeZone();
  return Array.from(new Set([here, "UTC"]));
}

/**
 * Timezone picker for the profile. The user's meeting-availability windows are
 * interpreted in this zone, so the negotiation books times the user is actually
 * free for regardless of who's asking.
 */
export function TimezoneField({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (tz: string) => void;
  disabled?: boolean;
}) {
  const options = React.useMemo(timezoneOptions, []);
  // Make sure the current value is selectable even if it's not in the list.
  const all = React.useMemo(
    () => (options.includes(value) ? options : [value, ...options]),
    [options, value],
  );

  return (
    <div className="space-y-2">
      {/* A heading, not a sentence. It read as an explanation ("the time set for where you work and
          what you see") while every other section on the page was named in two words, which is most
          of why this one looked like it belonged to a different form. */}
      <label htmlFor="timezone" className={`block ${SECTION_LABEL}`}>
        Timezone
      </label>
      <select
        id="timezone"
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
      >
        {all.map((tz) => (
          <option key={tz} value={tz}>
            {tz}
          </option>
        ))}
      </select>
    </div>
  );
}
